/**
 * Which channels an author may pick at send time, and the exact reason each of
 * the others cannot be picked.
 *
 * This is WP-CH-12's channel selector, kept as plain functions with no screen
 * around them. The Console will import it later. Keeping the decision separate
 * from the drawing means the same answer is given wherever it is asked, and it
 * can be tested without a browser.
 *
 * The governing rule: every known channel is listed and none is ever hidden.
 * A hidden channel tells an author nothing. A greyed one with a reason on it
 * tells them what to do next, or that there is nothing to do.
 */

import { CHANNELS } from '../utils/channelCatalogue';

/**
 * One row per channel, in catalogue order, whether the account has a binding on
 * it or not.
 *
 * `compatibility`, when supplied, is keyed by channel id and holds
 * `{compatible, blockers}` from ChannelGate's compatibility check. A survey that
 * cannot run on a channel makes that channel unselectable even when its binding
 * is perfectly healthy, because the send would fail on the first question the
 * channel cannot present.
 */
export function selectableChannels(bindings = [], { compatibility } = {}) {
    const all = Array.isArray(bindings) ? bindings : [];

    return CHANNELS.map((channel) => {
        const own = all.filter((b) => b.channel === (channel.bindingChannel || channel.id));
        const active = own.filter((b) => b.state === 'active');

        // The account's chosen default wins. Failing that, the oldest active
        // binding is used, so a send still has an identity rather than none.
        const marked = own.find((b) => b.is_default);
        const defaultBindingId = marked ? marked.binding_id : (active[0]?.binding_id ?? null);

        const compat = compatibility && compatibility[channel.id];
        const compatible = compat ? compat.compatible !== false : true;
        const blockers = compat?.blockers || [];

        const selectable = !channel.comingSoon && active.length > 0 && compatible;

        return {
            id: channel.id,
            name: channel.name,
            abbr: channel.abbr,
            markColor: channel.markColor,
            metered: channel.metered,
            selectable,
            bindings: own,
            defaultBindingId,
            reason: selectable ? '' : refusalReason(channel, own, compatible, blockers),
        };
    });
}

/**
 * Why this channel cannot be picked, in one sentence an author can act on.
 *
 * The order matters. Several of these can be true at once, and the first one is
 * the one worth saying. Telling somebody a channel is "not finished being set
 * up" when the real answer is that the survey cannot run on it sends them to fix
 * the wrong thing.
 */
function refusalReason(channel, own, compatible, blockers) {
    if (channel.comingSoon) return 'Coming soon — not available yet.';
    if (own.length === 0) return 'Not set up yet. Add a binding on the Channels screen.';

    if (!compatible) {
        const n = blockers.length;
        return `This survey cannot run on ${channel.name}: ${n} question${n === 1 ? '' : 's'} it cannot present.`;
    }

    if (own.every((b) => b.state === 'paused')) return 'Paused. In-flight conversations finish; no new sends.';
    if (own.every((b) => b.state === 'disabled')) return 'Closed.';
    if (own.some((b) => b.state === 'credential_expired')) return 'The credential expired. An administrator must replace it.';
    if (own.some((b) => b.state === 'awaiting_platform')) return 'Waiting on the platform to approve it.';
    if (own.some((b) => b.state === 'draft' || b.state === 'validated')) return 'Not finished being set up.';
    return 'No binding is active.';
}

/**
 * Which channel to offer before the author chooses.
 *
 * Browser is preferred because every account has its emailed launch path from day one, so it is
 * the one choice that is almost never wrong. With no selectable channel at all
 * this answers null, and the caller shows why rather than a picker that does
 * nothing.
 */
export function defaultSelection(rows = []) {
    const usable = rows.filter((row) => row.selectable);
    if (usable.length === 0) return null;
    const browser = usable.find((row) => row.id === 'browser');
    return browser ? browser.id : usable[0].id;
}
